import { React, useEffect, useState } from "react";
import {
  FieldLayout,
  PageTitleWithOutSideBarLayout,
  RowEndLayout,
  RowLayout,
  StyledBlueButton,
  StyledTitleTextField,
  StyledWhiteButton,
  WithOutSideBarLayout,
} from "common/components";
import { MyInfoData } from "common/data/MyInfoData";
import { VerticalGap } from "common/const/gaps";
import { useFieldChange } from "common/hooks/useChange";
import api from "common/utils/api";
import { useLocation, useNavigate } from "react-router";
import { validateInputs } from "common/components/Input/validFunction";
import { PopUpScreen } from "common/views";
import { useTranslation } from "react-i18next";
import { showMessageModal } from "common/utils/modalController";
import {
  isBusinessOwner,
  hasPermissions,
  isAdminOwner,
} from "common/store/userSession";

const MyProfileScreen = () => {
  const hasPermissionsUpdate = hasPermissions("ACCOUNT", "isUpdate");
  const isAdmin = isAdminOwner();
  const isBusiness = isBusinessOwner();
  const location = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(location.state?.success ?? false);
  const [change, setChange] = useState(false);
  const [save, setSave] = useState({});

  const [firstName, setFirstName] = useFieldChange(
    MyInfoData.firstName,
    setChange,
  );
  const [lastName, setLastName] = useFieldChange(
    MyInfoData.lastName,
    setChange,
  );
  const [email, setEmail] = useFieldChange(MyInfoData.email, setChange);
  const [company, setCompany] = useFieldChange(MyInfoData.company, setChange);
  const [phoneNumber, setPhoneNumber] = useFieldChange(
    MyInfoData.phoneNumber,
    setChange,
  );
  const [address, setAddress] = useFieldChange(MyInfoData.address, setChange);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [defaultData, setDefaultData] = useState({});
  const [validateInputMessage, setValidateInputMessage] = useState(null);

  const initField = () => {
    setPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    setSave({
      firstName: firstName,
      lastName: lastName,
      email: email,
      company: company,
      phoneNumber: phoneNumber,
      address: address,
      password: password,
      confirmPassword: confirmPassword,
    });
  }, [
    firstName,
    lastName,
    email,
    company,
    phoneNumber,
    address,
    password,
    confirmPassword,
  ]);

  useEffect(() => {
    api
      .get("/api/v1.0/users")
      .then((res) => {
        const data = res.data.result;
        setDefaultData({
          firstName: data.userName.firstName,
          lastName: data.userName.lastName,
          email: data.email,
          companyName: data.company.companyName,
          phoneNumber: data.company.companyTel.phoneNumber,
          address:
            data.company.companyAddress.city +
            " " +
            data.company.companyAddress.state +
            " " +
            data.company.companyAddress.address1 +
            " " +
            data.company.companyAddress.address2,
          editData: {
            companyTel: data.company.companyTel,
            companyAddress: data.company.companyAddress,
            country: data.country,
          },
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const myInfoParameter = {
    firstName: {
      title: t("profile.text001"),
      placeholder: t("profile.text001"),
      $isRequired: false,
      $onChanged: setFirstName,
      readOnly: !isEdit,
      disabled: isEdit,
    },
    lastName: {
      title: t("profile.text002"),
      placeholder: t("profile.text002"),
      $isRequired: false,
      $onChanged: setLastName,
      readOnly: !isEdit,
      disabled: isEdit,
    },
    email: {
      title: t("profile.text003"),
      placeholder: t("profile.text003"),
      $isRequired: false,
      $onChanged: setEmail,
      readOnly: !isEdit,
      disabled: isEdit,
    },
    company: {
      title: t("profile.text004"),
      placeholder: t("profile.text004"),
      $isRequired: false,
      $onChanged: setCompany,
      readOnly: !isEdit,
      disabled: isEdit,
    },
    phoneNumber: {
      title: t("profile.text005"),
      placeholder: t("profile.text005"),
      $isRequired: false,
      validType: "number",
      onChange: (e) => {
        setPhoneNumber(e.target.value);
      },
      readOnly: !isEdit,
      disabled: false,
    },
    address: {
      title: t("profile.text006"),
      placeholder: t("profile.text006"),
      $isRequired: false,
      $onChanged: setAddress,
      readOnly: !isEdit,
      disabled: isEdit,
    },
    password: {
      title: t("profile.text007"),
      placeholder: t("profile.text007"),
      defaultValue: "",
      $isRequired: true,
      onChange: (e) => {
        setPassword(e.target.value);
      },
    },
    confirmPassword: {
      title: t("profile.text008"),
      placeholder: t("profile.text008"),
      defaultValue: "",
      $isRequired: true,
      onChange: (e) => {
        setConfirmPassword(e.target.value);
      },
    },
  };

  return (
    <WithOutSideBarLayout>
      {validateInputMessage && (
        <PopUpScreen
          buttons={[
            <StyledBlueButton
              text={t("profile.text009")}
              mode="short"
              onClick={() => {
                setValidateInputMessage(null);
              }}
            />,
          ]}
        >
          <h2>{validateInputMessage}</h2>
        </PopUpScreen>
      )}
      <PageTitleWithOutSideBarLayout
        title={t("profile.text011")}
        button={
          !isEdit && (
            <RowEndLayout>
              <StyledBlueButton
                text={t("profile.text012")}
                mode="short"
                onClick={() => {
                  navigate("/verify-password", {
                    state: { nextPage: "/profile" },
                  });
                }}
                disabled={!isBusiness && !isAdmin && !hasPermissionsUpdate}
              />
            </RowEndLayout>
          )
        }
      >
        <FieldLayout>
          <RowLayout>
            <StyledTitleTextField
              {...myInfoParameter.firstName}
              defaultValue={defaultData?.firstName}
            />
            <StyledTitleTextField
              {...myInfoParameter.lastName}
              defaultValue={defaultData?.lastName}
            />
          </RowLayout>
          <StyledTitleTextField
            {...myInfoParameter.email}
            defaultValue={defaultData?.email}
          />
          {isEdit && (
            <FieldLayout>
              <StyledTitleTextField
                {...myInfoParameter.password}
                type="password"
              />
              <StyledTitleTextField
                {...myInfoParameter.confirmPassword}
                type="password"
              />
            </FieldLayout>
          )}
          <StyledTitleTextField
            {...myInfoParameter.company}
            defaultValue={defaultData?.companyName}
          />
          <StyledTitleTextField
            {...myInfoParameter.phoneNumber}
            defaultValue={defaultData?.phoneNumber}
          />
          <StyledTitleTextField
            {...myInfoParameter.address}
            defaultValue={defaultData?.address}
          />
        </FieldLayout>
        {isEdit && (
          <RowEndLayout>
            <StyledBlueButton
              text={t("profile.text013")}
              mode="short"
              disabled={
                password !== confirmPassword ||
                password === "" ||
                confirmPassword === "" ||
                password.length < 8
              }
              onClick={() => {
                if (!validateInputs({ targetScroll: true }).isValid) {
                  setValidateInputMessage(
                    t("profile.text010").replace(
                      "${???}",
                      validateInputs().invalidTitle,
                    ),
                  );
                  return;
                }

                api
                  .patch("/api/v1.0/users", {
                    newPw: password,
                    newCountry: defaultData?.editData?.country,
                    newCompanyName: company,
                    newCompanyTel: defaultData?.editData?.companyTel,
                    newCompanyAddress: defaultData?.editData?.companyAddress,
                  })
                  .then((res) => {
                    console.log(res);
                    setIsEdit(!isEdit);
                    setChange(!change);
                    initField();
                  })
                  .catch((error) => {
                    console.log(error);
                    if (
                      error.response.message === "Forbidden" ||
                      error.response.status === 403
                    ) {
                      showMessageModal(error.response.data.message);
                    }
                  });
              }}
            />
            <StyledWhiteButton
              text={t("profile.text014")}
              mode="short"
              onClick={() => {
                setIsEdit(!isEdit);
              }}
            />
          </RowEndLayout>
        )}
        <VerticalGap height="2rem" />
      </PageTitleWithOutSideBarLayout>
    </WithOutSideBarLayout>
  );
};

export default MyProfileScreen;
